import React from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import type { Control } from "react-hook-form";
import type { PlotRequestTag } from "@/features/map/types";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { SizingWrapperStyle } from "@/components/Wrapper";
import type { MapPlotRequestTagFormData } from "@/features/mapPlotRequest/hooks/usePlotRequestEditor";

interface PlotRequestHeaderProps {
  name: string;
  tags: PlotRequestTag[];
  isEditingName: boolean;
  editedName: string;
  isEditingTags: boolean;
  mapPlotRequestTagOptions: string[];
  control: Control<MapPlotRequestTagFormData>;
  onStartEditingName: () => void;
  onCancelEditingName: () => void;
  onNameChange: (value: string) => void;
  onNameSave: () => void;
  onStartEditingTags: () => void;
  onCancelEditingTags: () => void;
  onTagsSave: () => void;
  /** 作成者の氏名。他のユーザーが作成したリストのときだけ渡す */
  userName?: string;
  /** 同じ会社の他のユーザーが作成したリストのとき true。識別名とタグの編集を出さない */
  readOnly?: boolean;
}

/**
 * プロットリクエスト詳細のヘッダー部分（識別名とタグの表示・編集）
 */
export const PlotRequestHeader: React.FC<PlotRequestHeaderProps> = ({
  name,
  tags,
  isEditingName,
  editedName,
  isEditingTags,
  mapPlotRequestTagOptions,
  control,
  onStartEditingName,
  onCancelEditingName,
  onNameChange,
  onNameSave,
  onStartEditingTags,
  onCancelEditingTags,
  onTagsSave,
  userName,
  readOnly = false,
}) => {
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableBody>
            <TableRow>
              <TableCell
                component="th"
                sx={{
                  width: "10%",
                  border: "none",
                  p: 1,
                  pl: 0,
                  pr: 2,
                }}
              >
                識別名
              </TableCell>
              <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                {isEditingName ? (
                  <Box sx={{ maxWidth: "400px" }}>
                    <Box sx={{ mb: 1 }}>
                      <TextField
                        type="text"
                        value={editedName}
                        onChange={(e) => {
                          onNameChange(e.target.value);
                        }}
                        size="small"
                        autoFocus
                        fullWidth
                        inputProps={{ maxLength: 256 }}
                        helperText={`${editedName.length}/256`}
                        sx={{
                          "& .MuiInputBase-input": {
                            padding: "8px",
                            fontSize: "14px",
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={onCancelEditingName}
                      >
                        キャンセル
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={onNameSave}
                      >
                        保存
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography>{name}</Typography>
                    {!readOnly && (
                      <IconButton size="small" onClick={onStartEditingName}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                sx={{
                  width: "10%",
                  border: "none",
                  p: 1,
                  pl: 0,
                  pr: 2,
                }}
              >
                タグ
              </TableCell>
              <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                {isEditingTags ? (
                  <Box sx={{ maxWidth: "400px" }}>
                    <Box sx={{ mb: 1 }}>
                      <CAutocompleteMultiFreeSoloTextField
                        control={control}
                        name="mapPlotRequestTags"
                        label=""
                        size={SizingWrapperStyle.INHERIT}
                        maxLength={20}
                        options={mapPlotRequestTagOptions}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={onCancelEditingTags}
                      >
                        キャンセル
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={onTagsSave}
                      >
                        保存
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      alignItems: "center",
                    }}
                  >
                    {tags && tags.length > 0 ? (
                      <>
                        {tags.map((tag) => (
                          <Chip key={tag.id} label={tag.name} size="small" />
                        ))}
                        {!readOnly && (
                          <IconButton size="small" onClick={onStartEditingTags}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          タグなし
                        </Typography>
                        {!readOnly && (
                          <IconButton size="small" onClick={onStartEditingTags}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </TableCell>
            </TableRow>
            {userName !== undefined && (
              <TableRow>
                <TableCell
                  component="th"
                  sx={{
                    width: "10%",
                    border: "none",
                    p: 1,
                    pl: 0,
                    pr: 2,
                  }}
                >
                  作成者
                </TableCell>
                <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                  <Typography>{userName}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};
